/**
 * PluginPage - Pagina generica para plugins
 * Renderiza CRUD dinamico basado en la configuracion del plugin
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { pluginsAPI } from '../utils/api';
import { useBreadcrumbs } from '../context/BreadcrumbContext';
import { toast } from 'sonner';
import {
  Package, Search, Plus, RefreshCw, Loader2, Edit, Trash2, X, ChevronLeft, Save
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const PluginPage = () => {
  const location = useLocation();
  const { setBreadcrumbs } = useBreadcrumbs();
  const [pluginId, setPluginId] = useState(null);
  const [pageConfig, setPageConfig] = useState(null);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });

  const token = localStorage.getItem('token');
  const apiHeaders = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };

  // Determinar el plugin_id a partir de la ruta
  useEffect(() => {
    const path = location.pathname;
    // Ruta: /plugin/{pluginId}
    const match = path.match(/\/plugin\/([^/]+)/);
    if (match) {
      setPluginId(match[1]);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (pluginId) loadAll();
  }, [pluginId]);

  // Push breadcrumbs to Header via context
  useEffect(() => {
    if (pageConfig?.breadcrumbs) {
      setBreadcrumbs(pageConfig.breadcrumbs);
    }
    return () => setBreadcrumbs(null);
  }, [pageConfig, setBreadcrumbs]);

  async function apiCall(path, options = {}) {
    const res = await fetch(`${API_BASE}/api/plugins/${pluginId}/api/${path}`, {
      headers: apiHeaders,
      ...options,
    });
    return res.json();
  }

  async function loadAll() {
    setLoading(true);
    try {
      const config = await apiCall('page-config');
      setPageConfig(config);
      const data = await apiCall(`${config.entity_name_plural || 'items'}?search=${search}`);
      setItems(data.data || []);
      try {
        const s = await apiCall('stats');
        setStats(s);
      } catch (_) {}
    } catch (e) {
      toast.error('Error al cargar datos del plugin');
    } finally {
      setLoading(false);
    }
  }

  async function handleRefresh() {
    setRefreshing(true);
    try {
      const data = await apiCall(`${pageConfig?.entity_name_plural || 'items'}?search=${search}`);
      setItems(data.data || []);
      const s = await apiCall('stats');
      setStats(s);
    } catch (_) {} finally { setRefreshing(false); }
  }

  function handleNew() {
    const emptyForm = {};
    (pageConfig?.columns || []).forEach(col => {
      if (!col.readonly) {
        emptyForm[col.field] = col.default || (col.type === 'number' ? 0 : '');
      }
    });
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(true);
  }

  function handleEdit(item) {
    const editForm = {};
    (pageConfig?.columns || []).forEach(col => {
      if (!col.readonly) editForm[col.field] = item[col.field] ?? '';
    });
    setForm(editForm);
    setEditingId(item.id);
    setShowForm(true);
  }

  async function handleSave() {
    setSaving(true);
    try {
      const entity = pageConfig?.entity_name_plural || 'items';
      const url = editingId ? `${entity}/${editingId}` : entity;
      const result = await apiCall(url, {
        method: editingId ? 'PUT' : 'POST',
        body: JSON.stringify(form),
      });
      if (result.success || result.data) {
        toast.success(editingId ? 'Registro actualizado' : 'Registro creado');
        setShowForm(false);
        setEditingId(null);
        handleRefresh();
      } else {
        toast.error(result.error || 'Error al guardar');
      }
    } catch (e) {
      toast.error('Error al guardar');
    } finally { setSaving(false); }
  }

  async function handleDelete() {
    if (!deleteDialog.id) return;
    try {
      const entity = pageConfig?.entity_name_plural || 'items';
      await apiCall(`${entity}/${deleteDialog.id}`, { method: 'DELETE' });
      toast.success('Registro eliminado');
      setDeleteDialog({ open: false, id: null });
      handleRefresh();
    } catch (e) {
      toast.error('Error al eliminar');
    }
  }

  const tableColumns = useMemo(() => {
    if (!pageConfig) return [];
    const tCols = pageConfig.table_columns || pageConfig.columns?.map(c => c.field) || [];
    return tCols.map(field => pageConfig.columns?.find(c => c.field === field)).filter(Boolean);
  }, [pageConfig]);

  const estadoColors = pageConfig?.estado_colors || {};

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="plugin-page-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#9d8272]" />
      </div>
    );
  }

  if (!pageConfig) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-[#888]" data-testid="plugin-page-error">
        <Package className="w-12 h-12 mb-4 text-[#c5b8a2]" />
        <p>El plugin no esta activo o no expone una interfaz</p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="plugin-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-[#333] flex items-center gap-2" data-testid="plugin-page-title">
            <Package className="w-6 h-6 text-[#9d8272]" />
            {pageConfig.title}
          </h1>
          {pageConfig.description && (
            <p className="text-sm text-[#888] mt-1">{pageConfig.description}</p>
          )}
        </div>
        <Button onClick={handleNew} className="bg-[#9d8272] hover:bg-[#8c7161] text-white" data-testid="plugin-new-btn">
          <Plus className="w-4 h-4 mr-2" /> Nueva {pageConfig.entity_name || 'entrada'}
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {stats.total !== undefined && <StatCard label="Total" value={stats.total} color="#9d8272" />}
          {stats.por_estado && Object.entries(stats.por_estado).map(([k, v]) => (
            <StatCard key={k} label={k} value={v} color={
              k === 'Aprobada' ? '#16a34a' : k === 'Pendiente' ? '#d97706' : k === 'Rechazada' ? '#dc2626' : '#6b7280'
            } />
          ))}
          {stats.monto_aprobado !== undefined && (
            <StatCard label="Monto Aprobado" value={`S/ ${stats.monto_aprobado.toLocaleString()}`} color="#9d8272" />
          )}
        </div>
      )}

      {/* Filtros */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#888]" />
              <Input
                placeholder="Buscar..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleRefresh()}
                className="pl-10 border-[#c5b8a2]"
                data-testid="plugin-search"
              />
            </div>
            <Button variant="outline" onClick={handleRefresh} disabled={refreshing} className="border-[#c5b8a2]" data-testid="plugin-refresh-btn">
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabla */}
      <Card>
        <div className="relative">
          {refreshing && (
            <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center">
              <Loader2 className="w-5 h-5 animate-spin text-[#9d8272]" />
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="plugin-table">
              <thead>
                <tr className="bg-[#f8f6f4] border-b-2 border-[#e5e0da]">
                  {tableColumns.map(col => (
                    <th key={col.field} className="px-4 py-3 text-left text-xs font-semibold text-[#555]" style={{ width: col.width }}>
                      {col.label}
                    </th>
                  ))}
                  <th className="px-4 py-3 text-right text-xs font-semibold text-[#555]" style={{ width: '100px' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={tableColumns.length + 1} className="px-4 py-12 text-center text-[#aaa]">
                      No hay registros
                    </td>
                  </tr>
                ) : items.map(item => (
                  <tr key={item.id} className="border-b border-[#f0ece8] hover:bg-[#faf8f6] transition-colors" data-testid={`plugin-row-${item.id}`}>
                    {tableColumns.map(col => (
                      <td key={col.field} className="px-4 py-3 text-sm text-[#444]">
                        {col.field === 'estado' && estadoColors[item[col.field]] ? (
                          <Badge variant="outline" className={`${estadoColors[item[col.field]]?.bg || ''} ${estadoColors[item[col.field]]?.text || ''} ${estadoColors[item[col.field]]?.border || ''}`}>
                            {item[col.field]}
                          </Badge>
                        ) : col.field === 'monto' ? (
                          `S/ ${Number(item[col.field] || 0).toLocaleString()}`
                        ) : col.field === 'numero' ? (
                          <span className="font-semibold text-[#333]">{item[col.field]}</span>
                        ) : (
                          item[col.field]
                        )}
                      </td>
                    ))}
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(item)} className="text-[#9d8272]" data-testid={`edit-${item.id}`}>
                          <Edit className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setDeleteDialog({ open: true, id: item.id })} className="text-red-500" data-testid={`delete-${item.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Card>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" data-testid="plugin-form-modal">
          <div className="bg-white rounded-xl p-6 w-[640px] max-w-[95vw] max-h-[90vh] overflow-auto shadow-xl">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold text-[#333]">
                {editingId ? 'Editar' : 'Nueva'} {pageConfig.entity_name || 'entrada'}
              </h2>
              <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}><X className="w-4 h-4" /></Button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {(pageConfig.columns || []).filter(c => !c.readonly).map(col => (
                <div key={col.field} className={col.type === 'textarea' ? 'col-span-2' : ''}>
                  <label className="text-xs font-semibold text-[#555] mb-1 block">
                    {col.label} {col.required && <span className="text-red-500">*</span>}
                  </label>
                  {col.type === 'select' ? (
                    <select
                      className="w-full px-3 py-2 border border-[#c5b8a2] rounded-md text-sm"
                      value={form[col.field] || ''}
                      onChange={e => setForm({ ...form, [col.field]: e.target.value })}
                      data-testid={`field-${col.field}`}
                    >
                      <option value="">Seleccionar...</option>
                      {(col.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : col.type === 'textarea' ? (
                    <textarea
                      className="w-full px-3 py-2 border border-[#c5b8a2] rounded-md text-sm min-h-[60px] resize-y"
                      value={form[col.field] || ''}
                      onChange={e => setForm({ ...form, [col.field]: e.target.value })}
                      data-testid={`field-${col.field}`}
                    />
                  ) : (
                    <Input
                      type={col.type === 'number' ? 'number' : 'text'}
                      className="border-[#c5b8a2]"
                      value={form[col.field] ?? ''}
                      onChange={e => setForm({ ...form, [col.field]: col.type === 'number' ? Number(e.target.value) : e.target.value })}
                      data-testid={`field-${col.field}`}
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 mt-5 pt-4 border-t border-[#e5e0da]">
              <Button variant="outline" onClick={() => setShowForm(false)} className="border-[#c5b8a2]">Cancelar</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-[#9d8272] hover:bg-[#8c7161] text-white" data-testid="plugin-save-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                {editingId ? 'Actualizar' : 'Crear'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={o => !o && setDeleteDialog({ open: false, id: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-500" /> Eliminar registro
            </AlertDialogTitle>
            <AlertDialogDescription>
              Esta accion no se puede deshacer. El registro sera eliminado permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#c5b8a2]">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-btn">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const StatCard = ({ label, value, color }) => (
  <Card>
    <CardContent className="p-3 flex items-center gap-3">
      <div className="w-2 h-10 rounded-full" style={{ backgroundColor: color }} />
      <div>
        <p className="text-lg font-bold text-[#333]">{value}</p>
        <p className="text-xs text-[#888]">{label}</p>
      </div>
    </CardContent>
  </Card>
);

export default PluginPage;
